import { defaultRootPath, getDefaultPropfind, resultToNode as davResultToNode } from '@nextcloud/files/dav';
import { CancelablePromise } from 'cancelable-promise';
import { join } from 'path';
import { client } from './WebdavClient.ts';
import { searchNodes } from './WebDavSearch.ts';
import { getPinia } from '../store/index.ts';
import { useFilesStore } from '../store/files.ts';
import { useSearchStore } from '../store/search.ts';
import logger from '../logger.ts';
/**
 * Slim wrapper over `@nextcloud/files` `davResultToNode` to allow using the function with `Array.map`
 * @param stat The result returned by the webdav library
 */
export const resultToNode = (stat) => davResultToNode(stat);
/**
 * Get contents implementation for the files view.
 * This also allows to fetch local search results when the user is currently filtering.
 *
 * @param path - The path to query
 */
export function getContents(path = '/') {
    const controller = new AbortController();
    const searchStore = useSearchStore(getPinia());
    if (searchStore.query.length >= 3) {
        return new CancelablePromise((resolve, reject, cancel) => {
            cancel(() => controller.abort());
            getLocalSearch(path, searchStore.query, controller.signal)
                .then(resolve)
                .catch(reject);
        });
    }
    else {
        return defaultGetContents(path);
    }
}
/**
 * Generic `getContents` implementation for the users files.
 *
 * @param path - The path to get the contents
 */
export function defaultGetContents(path) {
    path = join(defaultRootPath, path);
    const controller = new AbortController();
    const propfindPayload = getDefaultPropfind();
    return new CancelablePromise(async (resolve, reject, onCancel) => {
        onCancel(() => controller.abort());
        try {
            const contentsResponse = await client.getDirectoryContents(path, {
                details: true,
                data: propfindPayload,
                includeSelf: true,
                signal: controller.signal,
            });
            const root = contentsResponse.data[0];
            const contents = contentsResponse.data.slice(1);
            if (root.filename !== path && `${root.filename}/` !== path) {
                logger.debug(`Exepected "${path}" but got filename "${root.filename}" instead.`);
                throw new Error('Root node does not match requested path');
            }
            resolve({
                folder: resultToNode(root),
                contents: contents.map((result) => {
                    try {
                        return resultToNode(result);
                    }
                    catch (error) {
                        logger.error(`Invalid node detected '${result.basename}'`, { error });
                        return null;
                    }
                }).filter(Boolean),
            });
        }
        catch (error) {
            reject(error);
        }
    });
}
/**
 * Get the local search results for the current folder.
 *
 * @param path - The path
 * @param query - The current search query
 * @param signal - The aboort signal
 */
async function getLocalSearch(path, query, signal) {
    const filesStore = useFilesStore(getPinia());
    let folder = filesStore.getDirectoryByPath('files', path);
    if (!folder) {
        const rootPath = join(defaultRootPath, path);
        const stat = await client.stat(rootPath, { details: true });
        folder = resultToNode(stat.data);
    }
    const contents = await searchNodes(query, { dir: path, signal });
    return {
        folder,
        contents,
    };
}
